// =============================================================================
// GLOBAL PATHS (shared across all organizations)
// =============================================================================
export const USERSPATH = 'users';
export const GENRESPATH = 'genres';
export const BPMPATH = 'bpm';
export const KEYSPATH = 'keys';
export const TAGSPATH = 'tags';
export const ORGANIZATIONSPATH = 'organizations';
export const AGENCYPATH = 'agency';    // Global for cross-org normalization
export const CLIENTPATH = 'client';    // Global for cross-org normalization
export const GLOBAL_ARTISTPATH = 'artists-global';  // Global for cross-org artist linking
export const BROADCASTERPATH = 'broadcaster';  // Global for cross-org broadcaster/station linking
export const MEDIAOWNERPATH = 'mediaowner';  // Global for cross-org media owner/media house linking
export const TERRITORIESPATH = 'territories';  // Global for license territories
export const FLIGHTINGPLATFORMPATH = 'flightingplatform';  // Global for cross-org normalization
export const MASTER_CATALOGUEPATH = 'masterCatalogue';  // All tracks uploaded across the platform
export const PUBLIC_CATALOGUEPATH = 'publicCatalogue';  // Only broadcaster-accepted tracks

// =============================================================================
// ORGANIZATION-SCOPED COLLECTION NAMES
// These collections are now nested under /organizations/{orgId}/
// =============================================================================
export const AUDIOMETAPATH = 'audiometa';
export const AUDIOFILESPATH = 'audiofiles';
export const VAULTNAMEPATH = 'vaultname';
export const COMPOSERPATH = 'composers';
export const STUDIOPATH = 'studio';
export const BACKUPLOCATIONPATH = 'backuplocation';
export const SESSIONARTISTSPATH = 'sessionartists';
export const PRODUCERPATH = 'producer';
export const CREATIVEDIRECTORPATH = 'creativedirector';
export const ARTISTSPATH = 'artists';
export const RECORDLABELSPATH = 'recordlabels';
export const PUBLISHERSPATH = 'publishers';
export const ALBUMSPATH = 'albums';

// =============================================================================
// STORAGE PATHS
// =============================================================================
// Legacy (deprecated): 'audio/{trackId}/'
// New multi-tenant: 'audio/{orgId}/{trackId}/'
export const AUDIOPATH = 'audio/';

// =============================================================================
// HELPER FUNCTIONS FOR ORGANIZATION-SCOPED PATHS
// =============================================================================

/**
 * Get the full Firestore path for an organization-scoped collection
 * @param {string} orgId - Organization ID
 * @param {string} collectionName - Name of the collection (e.g., 'audiometa', 'composers')
 * @returns {string} Full path like 'organizations/{orgId}/{collectionName}'
 */
export const getOrgCollectionPath = (orgId, collectionName) => {
  if (!orgId) {
    console.warn('getOrgCollectionPath called without orgId, using legacy path');
    return collectionName;
  }
  return `${ORGANIZATIONSPATH}/${orgId}/${collectionName}`;
};

/**
 * Get the full Storage path for organization-scoped audio files
 * @param {string} orgId - Organization ID
 * @param {string} trackId - Track/Audio meta ID
 * @returns {string} Storage path like 'audio/{orgId}/{trackId}/'
 */
export const getOrgStoragePath = (orgId, trackId) => {
  if (!orgId) {
    console.warn('getOrgStoragePath called without orgId, using legacy path');
    return `${AUDIOPATH}${trackId}/`;
  }
  return `${AUDIOPATH}${orgId}/${trackId}/`;
};

/**
 * Check if a collection is organization-scoped
 * @param {string} collectionName - Name of the collection
 * @returns {boolean} True if the collection should be org-scoped
 */
export const isOrgScopedCollection = (collectionName) => {
  const orgScopedCollections = [
    AUDIOMETAPATH,
    AUDIOFILESPATH,
    VAULTNAMEPATH,
    COMPOSERPATH,
    STUDIOPATH,
    BACKUPLOCATIONPATH,
    SESSIONARTISTSPATH,
    PRODUCERPATH,
    CREATIVEDIRECTORPATH,
    ARTISTSPATH,
    RECORDLABELSPATH,
    PUBLISHERSPATH,
    ALBUMSPATH,
  ];
  return orgScopedCollections.includes(collectionName);
};

/**
 * Check if a collection is global (shared across all orgs)
 * @param {string} collectionName - Name of the collection
 * @returns {boolean} True if the collection is global
 */
export const isGlobalCollection = (collectionName) => {
  const globalCollections = [
    USERSPATH,
    GENRESPATH,
    BPMPATH,
    KEYSPATH,
    TAGSPATH,
    AGENCYPATH,              // Global for cross-org normalization
    CLIENTPATH,              // Global for cross-org normalization
    GLOBAL_ARTISTPATH,       // Global for cross-org artist linking
    TERRITORIESPATH,         // Global for license territories
    FLIGHTINGPLATFORMPATH,   // Global for cross-org normalization
    BROADCASTERPATH,         // Global for cross-org broadcaster/station linking
    MEDIAOWNERPATH,          // Global for cross-org media owner linking
    MASTER_CATALOGUEPATH,    // All tracks uploaded across the platform
    PUBLIC_CATALOGUEPATH,    // Only broadcaster-accepted tracks
  ];
  return globalCollections.includes(collectionName);
};
